/*
 * air-allproducts.js — native behaviour for the converted pulsio-eu all-products page.
 * Replaces the Replo runtime for the interactions this page uses:
 *   1. Product cards navigate to their product page on click.
 *   2. Sleek-style scroll reveals (CSS transitions + IntersectionObserver).
 *   3. Collapsibles / accordions ([data-replo-collapsible]) toggle open/closed.
 *   4. Variant option-select swatches ([data-replo-option-select]) toggle active state.
 *   5. Tabs triggers ([data-replo-tabs-trigger]).
 * Marquee is pure CSS (@keyframes replo-marquee). No external dependencies. Idempotent.
 */
(function () {
  if (window.__airAllProductsInit) return;
  window.__airAllProductsInit = true;

  var REVEAL_OPTS = { rootMargin: '0px 0px -8% 0px', threshold: 0.15 };
  var STAGGER_MS = 80;
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  // ---- scroll reveals -------------------------------------------------------
  function hasAlchemyPaused(el) {
    var cs = getComputedStyle(el);
    return cs.animationName && cs.animationName.indexOf('alchemy') !== -1 && cs.animationPlayState === 'paused';
  }
  function hasBackgroundImage(el) {
    if ((el.style.backgroundImage || '').indexOf('url') !== -1) return true;
    var cs = getComputedStyle(el);
    return cs.backgroundImage && cs.backgroundImage !== 'none';
  }
  function isInView(el) {
    var r = el.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight;
    return r.top < vh * 0.92 && r.bottom > 0;
  }
  function revealEl(el) {
    if (el.__airRevealed) return;
    el.__airRevealed = true;
    el.classList.remove('air-reveal-pending');
    el.classList.add('air-reveal-visible');
  }
  function tagReveal(el, variant, delayMs) {
    if (el.__airRevealTagged) return null;
    el.__airRevealTagged = true;
    el.setAttribute('data-air-reveal', variant || 'fade-up');
    if (delayMs) el.style.setProperty('--air-reveal-delay', delayMs + 'ms');
    el.classList.add('air-reveal-pending', 'air-reveal--' + (variant || 'fade-up'));
    if (reducedMotion || isInView(el)) { revealEl(el); return null; }
    return el;
  }

  function initMotion() {
    var toObserve = [];
    var alchemyBg = [];
    function observe(el) { if (el && !el.__airRevealed) toObserve.push(el); }

    document.querySelectorAll('[data-air-reveal]').forEach(function (el) {
      if (el.__airRevealTagged) return;
      var variant = el.getAttribute('data-air-reveal') || 'fade-up';
      var delay = parseInt(el.getAttribute('data-air-reveal-delay') || '0', 10);
      observe(tagReveal(el, variant, delay));
    });

    document.querySelectorAll('[id^="replo-section-"]').forEach(function (section) {
      var gridIndex = 0;
      section.querySelectorAll('[data-replo-product-handle]').forEach(function (card) {
        if (hasAlchemyPaused(card)) return;
        var parent = card.parentElement;
        if (parent && hasAlchemyPaused(parent)) return;
        observe(tagReveal(card, 'fade-up', gridIndex * STAGGER_MS));
        gridIndex++;
      });
      section.querySelectorAll('.trustpilot-widget').forEach(function (widget) {
        var wrap = widget.parentElement;
        if (!wrap || wrap.__airRevealTagged) return;
        observe(tagReveal(wrap, 'fade', 0));
      });
    });

    var nodes = document.querySelectorAll('[data-rid]');
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      if (node.__airRevealTagged) continue;
      if (!hasAlchemyPaused(node)) continue;
      if (hasBackgroundImage(node)) { alchemyBg.push(node); continue; }
      node.classList.add('air-reveal--alchemy-override');
      observe(tagReveal(node, 'fade-up', 0));
    }

    function playAlchemy(el) { el.style.animationPlayState = 'running'; }
    if (alchemyBg.length) {
      if (!('IntersectionObserver' in window) || reducedMotion) {
        alchemyBg.forEach(playAlchemy);
      } else {
        var aio = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) { if (e.isIntersecting) { playAlchemy(e.target); aio.unobserve(e.target); } });
        }, { rootMargin: '0px 0px -5% 0px', threshold: 0.01 });
        alchemyBg.forEach(function (el) { if (isInView(el)) playAlchemy(el); else aio.observe(el); });
      }
    }

    if (!toObserve.length) return;
    if (reducedMotion || !('IntersectionObserver' in window)) { toObserve.forEach(revealEl); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { if (e.isIntersecting) { revealEl(e.target); io.unobserve(e.target); } });
    }, REVEAL_OPTS);
    toObserve.forEach(function (el) { if (isInView(el)) revealEl(el); else io.observe(el); });
  }

  // ---- product card navigation ---------------------------------------------
  function productUrl(handle, card) {
    if (card) {
      var explicit = (card.getAttribute('data-replo-product-url') || '').trim();
      if (explicit) return explicit;
      var linked = card.querySelector(
        'a.air-product-card-media-link[href], a[href*="/products/"][href]'
      );
      if (linked) return linked.getAttribute('href');
    }
    var root =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
      '/';
    return root + 'products/' + handle;
  }

  function initProductNav(scope) {
    var cards = (scope || document).querySelectorAll('[data-replo-product-handle]');
    Array.prototype.forEach.call(cards, function (card) {
      if (card.__airNav) return;
      var handle = (card.getAttribute('data-replo-product-handle') || '').trim();
      if (!handle || handle === 'empty-product') return;
      card.__airNav = true;
      card.style.cursor = 'pointer';
      card.addEventListener('click', function (e) {
        if (e.target.closest('a[href], button, [role="option"], [data-replo-option-select], [data-replo-tabs-trigger], [data-replo-collapsible-trigger]')) return;
        window.location.href = productUrl(handle, card);
      });
    });
  }

  // ---- collapsibles / accordions -------------------------------------------
  function findTrigger(root) {
    return (
      root.querySelector('[data-replo-collapsible-trigger]') ||
      root.querySelector('[role="button"]') ||
      root.firstElementChild
    );
  }
  function findContent(root) {
    return root.querySelector('.replo-animated') || root.querySelector('[data-replo-collapsible-content]');
  }
  function initCollapsibles(scope) {
    (scope || document).querySelectorAll('[data-replo-collapsible]').forEach(function (root) {
      if (root.__airCollapsible) return;
      root.__airCollapsible = true;
      var content = findContent(root);
      var trigger = findTrigger(root);
      if (!content || !trigger) return;
      var open = content.getAttribute('data-state') !== 'closed';
      function apply() {
        content.setAttribute('data-state', open ? 'open' : 'closed');
        content.style.display = open ? '' : 'none';
        trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      }
      apply();
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        open = !open;
        if (open) content.style.display = '';
        content.setAttribute('data-state', open ? 'open' : 'closed');
        trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (!open) {
          var hide = function () { if (content.getAttribute('data-state') === 'closed') content.style.display = 'none'; content.removeEventListener('animationend', hide); };
          content.addEventListener('animationend', hide);
          setTimeout(hide, 260);
        }
      });
    });
  }

  // ---- variant option-select swatches --------------------------------------
  function initOptionSelects(scope) {
    (scope || document).querySelectorAll('[data-replo-option-select]').forEach(function (listbox) {
      if (listbox.__airOptions) return;
      listbox.__airOptions = true;
      var options = Array.prototype.slice.call(listbox.querySelectorAll('[role="option"]'));
      if (!options.length) return;
      function select(opt) {
        options.forEach(function (o) {
          var on = o === opt;
          o.setAttribute('aria-selected', on ? 'true' : 'false');
          o.classList.toggle('r-trveid', on);
        });
      }
      options.forEach(function (opt) {
        opt.addEventListener('click', function (e) { e.stopPropagation(); select(opt); });
        opt.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); select(opt); }
        });
      });
    });
  }

  // ---- tabs ----------------------------------------------------------------
  function initTabs(scope) {
    var triggers = Array.prototype.slice.call((scope || document).querySelectorAll('[data-replo-tabs-trigger]'));
    triggers.forEach(function (trigger) {
      if (trigger.__airTab) return;
      trigger.__airTab = true;
      trigger.addEventListener('click', function () {
        triggers.forEach(function (t) { t.setAttribute('data-state', t === trigger ? 'active' : 'inactive'); });
      });
    });
  }

  ready(function () {
    initProductNav(document);
    initCollapsibles(document);
    initOptionSelects(document);
    initTabs(document);
    initMotion();
  });
})();
